import { useAuth } from '../../contexts/AuthContext';
import Loader from '../../components/loader/Loader';
import MainSteps from './mainSteps/MainSteps';
import ProjectHeader from './common/ProjectHeader';
import ProjectsNotFound from './common/ProjectsNotFound';
import StepDescription from './common/StepDescription';
import SubStepPanel from './common/SubStepPanel';
import SubSteps from './subSteps/SubSteps';
import { Box, CircularProgress } from '@mui/material';
import { ChevronRight } from 'react-feather';
import {
  fetchSettingsData,
  postSettingsData,
} from '../../redux/slice/projectSetting';
import {
  setProjectAndRun,
} from '../../redux/slice/userProjectSlice';
import { resolveDashboardProject } from '../../utils/resolveDashboardProject';
import { getProjectRun } from '../../services/projectRunsApi';
import { PrimaryButton } from '../../components/atoms/button/primaryButton/PrimaryButton';
import { pxToRem } from '../../utils/getFontValue';
import { RootState, useAppDispatch, useAppSelector } from '../../redux/store';
import { styled } from '@mui/system';
import { updateDashboardContent } from '../../redux/slice/dasboardContentSlice';
import { useEffect, useState } from 'react';
import {
  fetchStepInfoAction,
  updateStepInfoAction,
} from '../../redux/slice/stepsSlice';
import { initReports } from '../../redux/slice/viewReportSlice';
import { clearUploadersForProject } from '../../redux/slice/fileUploadSlice';
import { resetCoreAndRelatedToInitial } from '../../redux/slice/coreAndrelatedTableSlice';
import { fetchAppSettings } from '../../redux/slice/appSettings';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pageRoutes } from '../../constants/routes';

const DashboardContainer = styled(Box)`
  padding: 0 ${pxToRem(46)};
`;

const DashboardBody = styled(Box)`
  margin-top: 1rem;
`;

const SubStepsContainer = styled(Box)`
  display: flex;
  margin-top: 1.25rem;
`;

const SubStepBodyContainer = styled(Box)`
  width: 100%;
  overflow: hidden;
`;

const SubStepBody = styled(Box)`
  flex: 1;
  height: 100%;
`;

const SubStepTabContainer = styled(Box)`
  width: 100%;
  max-width: 13.813rem;
  margin-right: 1.25rem;
`;

const ButtonContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  padding: 2rem 0 0 0;
`;

const NextButtonIcon = styled(ChevronRight)`
  width: ${pxToRem(18)};
  height: ${pxToRem(18)};
`;

function parsePositiveInt(raw: string | null): number {
  if (raw == null || raw === '') return NaN;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : NaN;
}

function Dashboard() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isOrphanOrgUser, isLoading, currentUser, isSwitchingOrganization } = useAuth();

  useEffect(() => {
    if (isLoading || !currentUser || !isOrphanOrgUser) return;
    navigate(pageRoutes.onboarding, { replace: true });
  }, [isLoading, currentUser, isOrphanOrgUser, navigate]);

  const routeProjectId = parsePositiveInt(searchParams.get('projectId'));
  const routeRunId = parsePositiveInt(searchParams.get('runId'));
  const hasValidRunQuery =
    Number.isFinite(routeProjectId) &&
    Number.isFinite(routeRunId);

  const state = useAppSelector((s) => s.stepsState);

  const showProjectsNotFoundScreen = useAppSelector(
    (s: RootState) => s.loader.showProjectsNotFoundScreen
  );

  const currentProject = useAppSelector(
    (s: RootState) => s.userProject.currentProject
  );
  const projectId = currentProject.project_id;
  const currentRunId = useAppSelector(
    (s: RootState) => s.userProject.currentRunId
  );
  const [scopeReady, setScopeReady] = useState(false);

  const projectSettings = useAppSelector(
    (s: RootState) => s.projectSetting
  );

  const scriptCommands = useAppSelector(
    (s: RootState) => s.commands.scriptCommands
  );

  const dashboardContent = useAppSelector(
    (s: RootState) => s.dashboardContent
  );

  const [isLastStep, setIsLastStep] = useState(false);

  const mainSteps = state.steps;
  const activeMainStepIndex = state.activeStepIndex;
  const activeMainStep = mainSteps[activeMainStepIndex];

  const subSteps = activeMainStep.steps ?? [];
  const activeSubStepIndex = activeMainStep.activeStepIndex;
  const activeSubStep = subSteps[activeSubStepIndex];

  const handleNextClick = () => {
    if (activeMainStepIndex === 0 && activeSubStepIndex === 0) {
      const projectSettingsState = {
        projectId: projectId,
        system: projectSettings.system,
        host: projectSettings.host,
        spec: projectSettings.spec,
      };
      dispatch(postSettingsData(projectSettingsState));
    }
    dispatch(
      updateStepInfoAction(
        activeSubStep,
        activeMainStepIndex,
        activeSubStepIndex,
        projectId,
        currentRunId
      )
    );
  };

  useEffect(() => {
    dispatch(fetchAppSettings());
  }, [dispatch]);

  useEffect(() => {
    if (hasValidRunQuery) return;
    navigate(pageRoutes.onboarding, { replace: true });
  }, [hasValidRunQuery, navigate]);

  useEffect(() => {
    if (!hasValidRunQuery) {
      setScopeReady(false);
      return;
    }

    if (
      projectId === routeProjectId &&
      currentRunId === routeRunId &&
      currentProject.project_name
    ) {
      setScopeReady(true);
      return;
    }

    if (isSwitchingOrganization) {
      setScopeReady(false);
      return;
    }

    let cancelled = false;
    setScopeReady(false);

    (async () => {
      const project = await resolveDashboardProject(
        routeProjectId,
        currentProject
      );
      if (cancelled) return;
      if (!project) {
        navigate(pageRoutes.onboardingProjects, { replace: true });
        return;
      }
      try {
        await getProjectRun(routeProjectId, routeRunId);
      } catch {
        navigate(pageRoutes.onboardingProjects, { replace: true });
        return;
      }
      dispatch(
        setProjectAndRun({ project, runId: routeRunId })
      );
      setScopeReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    hasValidRunQuery,
    routeProjectId,
    routeRunId,
    projectId,
    currentRunId,
    currentProject.project_id,
    currentProject.project_name,
    currentProject,
    isSwitchingOrganization,
    navigate,
  ]);

  useEffect(() => {
    if (projectId <= 0) return;
    dispatch(fetchSettingsData(projectId));
  }, [dispatch, projectId]);

  useEffect(() => {
    dispatch(updateDashboardContent({
      scriptCommands,
      dbType: projectSettings.system
    }));
  }, [dispatch, scriptCommands, projectSettings.system]);

  useEffect(() => {
    let val: boolean = false;
    const lastStepIndex = state.steps.length - 1;
    if (activeMainStepIndex === lastStepIndex) {
      const lastStep = state.steps[lastStepIndex];
      val =
        lastStep.steps && lastStep.steps.length > 0
          ? activeSubStepIndex === lastStep.steps.length - 1
          : true;
    }
    setIsLastStep(val);
  }, [activeMainStepIndex, activeSubStepIndex, state]);

  useEffect(() => {
    if (projectId <= 0 || currentRunId == null) return;
    dispatch(initReports());
    dispatch(resetCoreAndRelatedToInitial());
    dispatch(clearUploadersForProject(projectId));
    dispatch(fetchStepInfoAction(projectId, currentRunId));
  }, [dispatch, projectId, currentRunId]);

  if (!hasValidRunQuery) {
    return null;
  }

  return !scopeReady ||
    isSwitchingOrganization ||
    state.isStepStatusLoading ? (
    <Loader />
  ) : showProjectsNotFoundScreen && projectId <= 0 ? (
    <ProjectsNotFound />
  ) : (
    <>
      <DashboardContainer>
        <ProjectHeader />
        <DashboardBody paddingBottom={5}>
          <MainSteps steps={mainSteps} activeStep={activeMainStepIndex} />
          <Box>
            {dashboardContent?.[activeMainStep.id]?.stepDescription && (
              <StepDescription
                id={activeMainStep.id}
                data={dashboardContent?.[activeMainStep.id]?.stepDescription}
              />
            )}

            {subSteps && Array.isArray(subSteps) && subSteps.length > 0 && (
              <SubStepsContainer>
                <SubStepTabContainer>
                  <SubSteps steps={subSteps} activeStep={activeSubStepIndex} />
                </SubStepTabContainer>
                <SubStepBodyContainer>
                  <SubStepBody>
                    <SubStepPanel id={activeSubStep.id}></SubStepPanel>
                  </SubStepBody>
                </SubStepBodyContainer>
              </SubStepsContainer>
            )}
          </Box>
          {!isLastStep && (
            <ButtonContainer>
              <PrimaryButton
                variant="outlined"
                disabled={
                  state.isStepStatusLoading || state.isStepStatusSaving
                }
                onClick={handleNextClick}
                endIcon={
                  state.isStepStatusSaving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <NextButtonIcon />
                  )
                }
              >
                {state.isStepStatusSaving ? 'Saving…' : 'Next'}
              </PrimaryButton>
            </ButtonContainer>
          )}
        </DashboardBody>
      </DashboardContainer>
    </>
  );
}

export default Dashboard;
